import PropTypes from 'prop-types';
import {createUseStyles, useTheme} from 'react-jss';
import {connect} from 'react-redux';
import SubscriptionInformationSectionWrapper from '../../../../sections/SubscriptionInformationSection/SubscriptionInformationSectionWrapper';
import Button from '../../../../UI/Button/Button';
import {showOverlay} from '../../../actions/overlay-actions';
import Translation from '../../../lang/Translation';
import {isFastDeliverySelected} from '../../../selectors/DeliveryOptionsSelectors/DeliveryOptionsSelectors';
import getBasketContainsSubscriptionProduct from '../../../selectors/getBasketContainsSubscriptionProduct';
import getShouldShowLoyaltyPoints from '../../../selectors/getShouldShowLoyaltyPoints';
import getShouldShowReturnToBasketCTA from '../../../selectors/getShouldShowReturnToBasketCTA';
import {
  getLoyaltyPoints, getSettings, getSiteCode, isGuest,
} from '../../../selectors/selectors';
import replaceHTMLEntityWithSymbol from '../../../utils/replace-html-entity-with-symbol';
import BasketItemImageWrapper from '../../BasketItemImage/BasketItemImageWrapper';
import BasketItemImageWrapperV2 from '../../BasketItemImage/BasketItemImageWrapperV2/BasketItemImageWrapperV2';
import {TooltipAndOverlayIDs} from '../../detailed-tooltip/TooltipLibrary';
import ItemCount from '../../ProgressBar/StepSummary/ItemCount';
import ReturnToBasketCTA from '../../ReturnToBasketCTA/ReturnToBasketCTA';
import DownChevron from '../../ui/svg/Components/DownChevron';
import InfoIcon from '../../ui/svg/Components/InfoIcon';
import LoyaltyPointsLine from '../OrderSummarySection/LoyaltyPoints/LoyaltyPointsLine';

const useStyles = createUseStyles({
  orderSummaryHeader: {
    display: 'contents',
    width: '100%',
    cursor: 'pointer',
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    padding: ['2em', '1em'],
    alignItems: 'center',
    position: 'sticky',
    zIndex: 101,
    top: 0,
    backgroundColor: '#fff',
  },
  splitDeliveryText: {
    display: 'flex',
    flexDirection: 'row',
    padding: ['0', '1em', '2em', '1em'],
    alignItems: 'center',
    position: 'sticky',
    zIndex: 101,
    top: 0,
    backgroundColor: '#fff',
    textAlign: 'left',
  },
  orderTotal: {
    marginRight: 10,
  },
  summary: {
    fontSize: props => props.theme.orderSummary.mobileHeader.summaryFontSize,
    fontWeight: props => props.theme.core.boldFontWeight,
  },
  openSummary: {
    whiteSpace: 'nowrap',
    fontSize: props => props.theme.orderSummary.mobileHeader.openSummaryFontSize,
    display: 'flex',
    alignItems: 'center',
  },
  downChevron: {
    marginLeft: 10,
  },
  backToBasketWrapper: {
    backgroundColor: '#fff',
    height: 48,
    paddingLeft: 8,
  },
});

export function OrderSummaryHeader(props) {
  const theme = useTheme();
  const classes = useStyles({...props, theme});
  
  return (
    <div className={classes.orderSummaryHeaderWrapper}>
      
      {props.shouldShowReturnToBasketCTA
        && (
        <div className={classes.backToBasketWrapper}>
          <ReturnToBasketCTA />
        </div>
        )}
        
      <Button
        className={classes.orderSummaryHeader}
        id="OrderSummaryHeader"
        data-test="OrderSummaryHeader"
        onClick={props.openOrderSummaryOverlay}
        link
        noStyle
      >
        <div
          className={classes.header}
          data-test="OrderSummaryHeader__header"
        >
          {(props.showLoyaltyPoints && props.loyaltyPoints) ? (
            <LoyaltyPointsLine isHeader isGuest={props.isGuest} loyaltyPoints={props.loyaltyPoints} iconColor="#000" />
          ) : null}
          <div className={classes.summary}>
            <span className={classes.orderTotal} data-test="OrderSummaryHeader__orderTotal">
              {replaceHTMLEntityWithSymbol(props.orderTotal)}
            </span>
            <span data-test="OrderSummaryHeader__itemCount">
              <ItemCount count={props.itemCount} siteCode={props.siteCode} />
            </span>
          </div>
          <div className={classes.openSummary}>
            <Translation>orderSummary.view.order</Translation>
            <span className={classes.downChevron}>
              <DownChevron />
            </span>
          </div>
      
        </div>
        {props.splitDeliveryFee && (
        <div className={classes.splitDeliveryText}>
          <InfoIcon rightMargin />
          <Translation placeholderValues={[replaceHTMLEntityWithSymbol(props.splitDeliveryFee)]}>
            split.delivery.charge.added
          </Translation>
        </div>
        )}
      
        {props.useNewProductWrapper
          ? <BasketItemImageWrapperV2 basketItems={props.basketItems} />
          : <BasketItemImageWrapper basketItems={props.basketItems} />}
      </Button>
  
      {props.outputSubscription && <SubscriptionInformationSectionWrapper />}
    </div>
  );
}

OrderSummaryHeader.defaultProps = {};

OrderSummaryHeader.propTypes = {
  itemCount: PropTypes.string.isRequired,
  orderTotal: PropTypes.string.isRequired,
  basketItems: PropTypes.arrayOf(PropTypes.shape({
    productUrl: PropTypes.string,
    productName: PropTypes.string,
    quantity: PropTypes.number,
    totalPrice: PropTypes.string,
    discountedPrice: PropTypes.string,
  })).isRequired,
  splitDeliveryFee: PropTypes.number.isRequired,
  loyaltyPoints: PropTypes.number.isRequired,
  openOrderSummaryOverlay: PropTypes.func.isRequired,
  outputSubscription: PropTypes.bool,
  isFastDeliverySelected: PropTypes.bool.isRequired,
  showLoyaltyPoints: PropTypes.bool.isRequired,
  siteCode: PropTypes.string.isRequired,
  useNewProductWrapper: PropTypes.bool,
  isGuest: PropTypes.bool,
  shouldShowReturnToBasketCTA: PropTypes.bool,
};

const mapStateToProps = (state) => {
  const passPropIfNotZero = money => money?.value > 0 && money.formatted;
  
  return ({
    basketItems: state.orderSummary.basketItems,
    itemCount: state.orderSummary.itemCount,
    orderTotal: state.orderSummary.orderTotal.formatted,
    splitDeliveryFee: passPropIfNotZero(state.orderSummary.extraDeliveryFee),
    isFastDeliverySelected: isFastDeliverySelected(state),
    siteCode: getSiteCode(state),
    useNewProductWrapper: getSettings(state)?.use_new_product_wrapper,
    isGuest: isGuest(state),
    loyaltyPoints: getLoyaltyPoints(state),
    showLoyaltyPoints: getShouldShowLoyaltyPoints(state),
    outputSubscription: state.ui.checkoutProgressStep === state.ui.sections.length && getSettings(state)?.subscription_info_message_enabled && getBasketContainsSubscriptionProduct(state),
    shouldShowReturnToBasketCTA: getShouldShowReturnToBasketCTA(state),
  });
};

const mapDispatchToProps = dispatch => ({
  openOrderSummaryOverlay: () => dispatch(showOverlay(TooltipAndOverlayIDs.ORDERSUMMARY)),
});

export default connect(mapStateToProps, mapDispatchToProps)(OrderSummaryHeader);
